import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import moment from 'moment';
import ApiService from '../../../services/ApiService';
import Loader from '../../../components/Loader';
import { formatDate } from '../../../utils/helper';

const STATUS_FLOW = {
    pending: 'Confirmed',
    confirmed: 'Completed',
};

const statusBadge = (status) => {
    switch (String(status).toLowerCase()) {
        case 'completed': return 'badge-light-success';
        case 'cancelled': return 'badge-light-danger';
        case 'confirmed': return 'badge-light-primary';
        default: return 'badge-light-warning';
    }
};

const BookingDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const goBack = () => navigate(-1);

    const [isLoader, setIsLoader] = useState(false);
    const [booking, setBooking] = useState(null);

    useEffect(() => {
        getBookingDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const getBookingDetails = async () => {
        try {
            setIsLoader(true);
            const response = await ApiService.request({
                method: 'GET',
                url: `admin/bookings/${id}`,
            });

            if (response.data.status) {
                setBooking(response.data.data);
            } else {
                toast.error(response.data.message);
                navigate('/admin/bookings');
            }
        } catch (error) {
            toast.error('Failed to fetch booking details');
            navigate('/admin/bookings');
        } finally {
            setIsLoader(false);
        }
    };

    const updateStatus = async (status, extra = {}) => {
        try {
            setIsLoader(true);
            const response = await ApiService.request({
                method: 'POST',
                url: `admin/bookings/${id}/status`,
                data: { status, ...extra },
            });
            if (response.data.status) {
                toast.success(response.data.message);
                getBookingDetails();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoader(false);
        }
    };

    const handleAdvance = async () => {
        const next = STATUS_FLOW[String(booking.status).toLowerCase()];
        if (!next) return;

        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `Mark this booking as ${next}?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4BC95F',
            confirmButtonText: `Yes, mark ${next}`,
            cancelButtonColor: '#9E9B9B',
            cancelButtonText: 'No',
        });

        if (result.isConfirmed) updateStatus(next);
    };

    const handleCancel = async () => {
        const { value: formValues, isConfirmed } = await Swal.fire({
            title: 'Cancel booking',
            html:
                '<input id="swal-reason" class="swal2-input" placeholder="Cancellation reason">' +
                '<input id="swal-charge" type="number" min="0" class="swal2-input" placeholder="Cancellation charge">' +
                '<input id="swal-refund" type="number" min="0" class="swal2-input" placeholder="Refund amount">',
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Cancel booking',
            cancelButtonColor: '#9E9B9B',
            cancelButtonText: 'Back',
            preConfirm: () => ({
                cancellation_reason: document.getElementById('swal-reason').value,
                cancellation_charge: document.getElementById('swal-charge').value || 0,
                refund_amount: document.getElementById('swal-refund').value || 0,
            }),
        });

        if (isConfirmed) updateStatus('Cancelled', formValues);
    };

    if (isLoader && !booking) {
        return (
            <div className="d-flex justify-content-center align-items-center py-20">
                <Loader text="Loading booking" size={35} />
            </div>
        );
    }

    if (!booking) return null;

    const b = booking;
    const services = b.services || [];
    const bufferMinutes = Number(b.booking_buffer_minutes) || 0;
    const totalServiceMinutes = services.reduce((sum, s) => sum + (Number(s.duration) || 0), 0);
    // The room is held for the full slot — Duration below stays the full
    // session length — but the displayed end time is trimmed by the admin's
    // turnover buffer, same as the app's checkout review. See
    // Setting.booking_buffer_minutes.
    const bookingTimeRange = (() => {
        if (!b.booking_time) return 'N/A';
        const start = moment(b.booking_time, ['h:mm A', 'H:mm', 'HH:mm'], true);
        if (!start.isValid() || !totalServiceMinutes) return b.booking_time;
        const end = start.clone().add(Math.max(totalServiceMinutes - bufferMinutes, 0), 'minutes');
        return `${start.format('h:mm A')} - ${end.format('h:mm A')}`;
    })();
    const nextStatus = STATUS_FLOW[String(b.status).toLowerCase()];
    const isCancelled = String(b.status).toLowerCase() === 'cancelled';
    // Walk-in/guest bookings (Admin Booking Calendar) have no Cancel option —
    // the client is already present when the booking is created.
    const isWalkIn = b.booking_source === 'admin';

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                            Booking {b.booking_code || `#${String(b._id).slice(-6)}`}
                        </h1>
                        <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                            <li className="breadcrumb-item text-muted">
                                <Link to="/admin/dashboard" className="text-muted text-hover-primary">Home</Link>
                            </li>
                            <li className="breadcrumb-item">
                                <span className="bullet bg-gray-400 w-5px h-2px"></span>
                            </li>
                            <li className="breadcrumb-item text-muted">
                                <Link to="/admin/bookings" className="text-muted text-hover-primary">Bookings</Link>
                            </li>
                        </ul>
                    </div>
                    <div className="d-flex align-items-center gap-2 gap-lg-3">
                        <button onClick={goBack} className="btn btn-sm btn-light">
                            <i className="bi bi-arrow-left me-1"></i> Back
                        </button>
                        {nextStatus && (
                            <button onClick={handleAdvance} className="btn btn-sm btn-base">
                                Mark {nextStatus}
                            </button>
                        )}
                        {!isCancelled && !isWalkIn && (
                            <button onClick={handleCancel} className="btn btn-sm btn-light-danger">
                                Cancel Booking
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-xxl">
                    <div className="row g-6">

                        {/* Booking summary */}
                        <div className="col-lg-8">
                            <div className="card mb-6">
                                <div className="card-header">
                                    <h3 className="card-title fw-bold">Booking Information</h3>
                                    <div className="card-toolbar">
                                        <span className={`badge ${statusBadge(b.status)} text-uppercase`}>{b.status || 'N/A'}</span>
                                    </div>
                                </div>
                                <div className="card-body">
                                    <div className="row">
                                        <div className="col-md-4 mb-5">
                                            <span className="text-muted d-block fs-7">Booking Date</span>
                                            <span className="fs-6 text-gray-800">{formatDate(b.booking_date)}</span>
                                        </div>
                                        <div className="col-md-4 mb-5">
                                            <span className="text-muted d-block fs-7">Booking Time</span>
                                            <span className="fs-6 text-gray-800">{bookingTimeRange}</span>
                                        </div>
                                        <div className="col-md-4 mb-5">
                                            <span className="text-muted d-block fs-7">Paid By</span>
                                            <span className="fs-6 text-gray-800 text-capitalize">{b.payment_by || 'N/A'}</span>
                                        </div>
                                        <div className="col-md-4 mb-5">
                                            <span className="text-muted d-block fs-7">Payment Method</span>
                                            <span className="fs-6 text-gray-800 text-capitalize">
                                                {b.payment_method?.replace(/_/g, ' ') || 'N/A'}
                                            </span>
                                        </div>
                                        <div className="col-md-4 mb-5">
                                            <span className="text-muted d-block fs-7">Payment Status</span>
                                            <span className="fs-6 text-gray-800">{b.payment_status || 'N/A'}</span>
                                        </div>
                                        <div className="col-md-4 mb-5">
                                            <span className="text-muted d-block fs-7">Transaction ID</span>
                                            <span className="fs-6 text-gray-800">{b.transaction_id || 'N/A'}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="card mb-6">
                                <div className="card-header">
                                    <h3 className="card-title fw-bold">Room</h3>
                                </div>
                                <div className="card-body">
                                    <h4 className="text-dark fw-bold">{b.room_id?.title || 'N/A'    }</h4>
                                </div>
                            </div>


                            {/* Sessions */}
                            <div className="card mb-6">
                                <div className="card-header">
                                    <h3 className="card-title fw-bold">Sessions</h3>
                                </div>
                                <div className="card-body">
                                    <div className="table-responsive">
                                        <table className="table align-middle table-hover fs-6 gy-4">
                                            <thead>
                                                <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                                                    <th>Service</th>
                                                    <th>Room</th>
                                                    <th>Duration</th>
                                                </tr>
                                            </thead>
                                            <tbody className="fw-semibold text-gray-600">
                                                {services.length > 0 ? services.map((s, i) => (
                                                    <tr key={i}>
                                                        <td className="text-dark fw-bold">{s.service_id?.title || '—'}</td>
                                                        <td>{b.room_id?.title || '—'}</td>
                                                        <td>{s.duration ? `${s.duration} min` : '—'}</td>
                                                    </tr>
                                                )) : (
                                                    <tr>
                                                        <td colSpan="3" className="text-center text-muted">No sessions listed</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                            {/* Cancellation */}
                            {b.cancelled_at && (
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title fw-bold">Cancellation</h3>
                                    </div>
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-md-6 mb-5">
                                                <span className="text-muted d-block fs-7">Cancelled On</span>
                                                <span className="fs-6 text-gray-800">{formatDate(b.cancelled_at)}</span>
                                            </div>
                                            <div className="col-md-6 mb-5">
                                                <span className="text-muted d-block fs-7">Cancelled By</span>
                                                <span className="fs-6 text-gray-800 text-capitalize">{b.cancelled_by || 'N/A'}</span>
                                            </div>
                                            <div className="col-md-12 mb-5">
                                                <span className="text-muted d-block fs-7">Reason</span>
                                                <span className="fs-6 text-gray-800">{b.cancellation_reason || 'N/A'}</span>
                                            </div>
                                            <div className="col-md-6 mb-5">
                                                <span className="text-muted d-block fs-7">Cancellation Charge</span>
                                                <span className="fs-6 text-danger fw-bold">AED {b.cancellation_charge ?? 0}</span>
                                            </div>
                                            <div className="col-md-6 mb-5">
                                                <span className="text-muted d-block fs-7">Refund Amount</span>
                                                <span className="fs-6 text-success fw-bold">AED {b.refund_amount ?? 0}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Customer (or walk-in guest, for an admin-created manual booking) */}
                        <div className="col-lg-4">
                            <div className="card">
                                <div className="card-header">
                                    <h3 className="card-title fw-bold">Customer</h3>
                                </div>
                                <div className="card-body">
                                    {b.user_id ? (
                                        <>
                                            <div className="d-flex align-items-center mb-5">
                                                <img
                                                    src={b.user_id.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(b.user_id.name || 'U')}`}
                                                    alt={b.user_id.name}
                                                    className="w-60px h-60px rounded-circle me-4"
                                                />
                                                <div>
                                                    <Link to={`/admin/users/${b.user_id._id}`} className="fs-5 fw-bold text-dark text-hover-primary">
                                                        {b.user_id.name || 'N/A'}
                                                    </Link>
                                                </div>
                                            </div>
                                            <div className="mb-3">
                                                <span className="text-muted d-block fs-7">Email</span>
                                                <span className="fs-6 text-gray-800">{b.user_id.email || 'N/A'}</span>
                                            </div>
                                            <div>
                                                <span className="text-muted d-block fs-7">Phone</span>
                                                <span className="fs-6 text-gray-800">{b.user_id.phone || 'N/A'}</span>
                                            </div>
                                        </>
                                    ) : b.guest_name ? (
                                        <>
                                            <div className="d-flex align-items-center mb-5">
                                                <img
                                                    src={`https://ui-avatars.com/api/?name=${encodeURIComponent(b.guest_name)}`}
                                                    alt={b.guest_name}
                                                    className="w-60px h-60px rounded-circle me-4"
                                                />
                                                <div>
                                                    <span className="fs-5 fw-bold text-dark">{b.guest_name}</span>
                                                    <span className="badge badge-light-warning ms-2">Walking Customer</span>
                                                </div>
                                            </div>
                                            <div>
                                                <span className="text-muted d-block fs-7">Phone</span>
                                                <span className="fs-6 text-gray-800">{b.guest_phone || 'N/A'}</span>
                                            </div>
                                        </>
                                    ) : (
                                        <span className="text-muted">Customer no longer exists</span>
                                    )}
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default BookingDetails;
